import { HTMLAttributes, memo } from 'react';

import { merge } from '@/components';
import { WizardState } from '@/components/ui';

export type StepIndicatorProps = {
  max: number;
  current: number;
  startStep?: number;
  setCurrent: (p: number) => void;
  hideLast?: boolean;
  hasCompletedStep: (step: number) => boolean;
} & HTMLAttributes<HTMLDivElement>;

const IStepIndicator = ({
  max,
  current,
  startStep,
  setCurrent,
  hideLast,
  hasCompletedStep,
  ...rest
}: StepIndicatorProps) => {
  const start = startStep ?? 0;

  return (
    <div {...rest} className="mt-xs gap-sm flex justify-center">
      {Array.from({ length: max - (hideLast ? 1 : 0) }).map((_, index) => {
        const step = start + index;
        const isSelectable = Array.from({ length: step }).every((_, i) => hasCompletedStep(start + i));

        return (
          <div
            key={index}
            aria-selected={step === current}
            onClick={isSelectable ? () => setCurrent(step) : undefined}
            className={merge(
              'aria-[selected=true]:bg-primary-400 size-sm cursor-not-allowed rounded-full aria-[selected=false]:bg-gray-400',
              isSelectable && 'cursor-pointer'
            )}
          />
        );
      })}
    </div>
  );
};

export const StepIndicator = memo(IStepIndicator) as typeof IStepIndicator;

export interface WizardStepIndicatorProps<T> {
  useWizard: () => WizardState<T>;
  hideLast?: boolean;
}

const IWizardStepIndicator = <T extends object>({ useWizard }: WizardStepIndicatorProps<T>) => {
  const wizard = useWizard();
  return (
    <StepIndicator
      current={wizard.step}
      setCurrent={wizard.setStep}
      max={wizard.maxSteps}
      hideLast
      hasCompletedStep={wizard.hasCompletedStep}
    />
  );
};

export const WizardStepIndicator = memo(IWizardStepIndicator) as typeof IWizardStepIndicator;
